import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { RiskTag } from '@/components/chariot-ui';
import React, { useMemo } from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { Table } from '@/components/table/Table';
import { Column } from '@/components/table/types';
import { useAsyncExports } from '@/hooks/useAsyncExports';
import { getRiskTagSeverity } from '@/sections/vulnerabilities/components/VulnerabilityDrawer';

import { SmartStatusChip } from './ChatStatusChips';

interface ChatResultsTableProps {
  jsonData: any;
}

export const ChatResultsTable: React.FC<ChatResultsTableProps> = ({
  jsonData,
}) => {
  // Determine export type based on table name
  const getExportType = (
    name: string
  ): 'asset' | 'risk' | 'technology' | 'seed' | 'agent' => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('risk') || lowerName.includes('vulnerabilit'))
      return 'risk';
    if (lowerName.includes('tech')) return 'technology';
    if (lowerName.includes('seed')) return 'seed';
    if (lowerName.includes('agent')) return 'agent';
    return 'asset';
  };

  // Find the first array in the JSON data (including nested arrays)
  const getTableData = () => {
    // First check top-level arrays
    for (const [key, value] of Object.entries(jsonData)) {
      if (Array.isArray(value) && value.length > 0) {
        return { tableName: key, data: value };
      }
    }

    // If no top-level arrays, check for nested arrays in objects
    for (const [parentKey, parentValue] of Object.entries(jsonData)) {
      if (
        parentValue &&
        typeof parentValue === 'object' &&
        !Array.isArray(parentValue)
      ) {
        for (const [nestedKey, nestedValue] of Object.entries(parentValue)) {
          if (Array.isArray(nestedValue) && nestedValue.length > 0) {
            return { tableName: nestedKey, data: nestedValue };
          }
        }
      }
    }

    return null;
  };

  const tableData = useMemo(() => getTableData(), [jsonData]);

  if (!tableData) return null;

  const { tableName, data } = tableData;

  // Get export hook based on table type
  const exportType = getExportType(tableName);
  const { mutateAsync: exportData, isPending: isExporting } =
    useAsyncExports(exportType);

  // Dynamically determine columns from the data - memoized to prevent re-renders
  const columns = useMemo((): Column<any>[] => {
    const generateColumns = (items: any[]): Column<any>[] => {
      if (items.length === 0) return [];

      // Get all keys from first few items
      const sampleSize = Math.min(3, items.length);
      const keySet = new Set<string>();

      for (let i = 0; i < sampleSize; i++) {
        const item = items[i];
        if (item && typeof item === 'object') {
          Object.keys(item).forEach(key => keySet.add(key));
        }
      }

      // Detect if this is risk data (has priority field which indicates severity)
      const isRiskData = keySet.has('priority') && keySet.has('status');

      // Filter out internal/noisy fields - be more specific to avoid over-filtering
      const excludePatterns = [
        'username',
        'ttl',
        'private',
        'history',
        'parent',
        'origin_source',
      ];
      const exactExcludes = ['key']; // Only exclude if field name is exactly 'key'

      const relevantKeys = Array.from(keySet).filter(key => {
        // Exclude if exact match
        if (exactExcludes.includes(key)) return false;
        // Exclude if contains pattern
        return !excludePatterns.some(pattern => key.includes(pattern));
      });

      // Smart field prioritization based on common Chariot entities
      const getFieldPriority = (field: string): number => {
        const priorities: Record<string, number> = {
          name: 1,
          dns: 2,
          identifier: 3,
          url: 4,
          title: 5,
          status: 6,
          priority: 7,
          class: 8,
          source: 9,
          capabilities: 10,
          capability: 11,
          value: 12,
          target: 13,
          secret: 14,
          created: 15,
          updated: 16,
          visited: 17,
        };
        return priorities[field] || 999;
      };

      const sortedKeys = relevantKeys
        .sort((a, b) => getFieldPriority(a) - getFieldPriority(b))
        .slice(0, 5); // Limit to 5 most relevant columns

      return sortedKeys.map(key => ({
        id: key,
        label:
          key === 'priority' && isRiskData
            ? 'Severity'
            : key.charAt(0).toUpperCase() +
              key.slice(1).replace(/([A-Z])/g, ' $1'),
        cell: (item: any) => {
          const value = item[key];

          if (value === null || value === undefined || value === '') {
            return (
              <div className="group flex items-center justify-between">
                <span>-</span>
              </div>
            );
          }

          // For risk data, handle priority as severity and status separately
          if (isRiskData && key === 'priority') {
            return (
              <div className="group flex items-center justify-between">
                <RiskTag severity={getRiskTagSeverity(item.status)} size="sm" />
              </div>
            );
          }

          // Check if this is a status field that needs a chip
          const isStatusField =
            key === 'status' || key.toLowerCase().includes('status');
          // For risk data, don't pass priority to status chip since severity is shown separately
          const priority = isRiskData
            ? undefined
            : item.priority || item.severity;

          // Get the raw value for copying and display value
          let copyValue: string = '';
          let displayValue: string = '';

          // Handle different value types
          if (typeof value === 'boolean') {
            copyValue = value.toString();
            displayValue = value ? 'Yes' : 'No';
          } else if (typeof value === 'number') {
            copyValue = value.toString();
            displayValue = value.toString();
          } else if (typeof value === 'object') {
            copyValue = JSON.stringify(value, null, 2);
            // For arrays, show joined values if small, otherwise show count
            if (Array.isArray(value)) {
              if (
                value.length <= 3 &&
                value.every(v => typeof v === 'string' && v.length <= 20)
              ) {
                displayValue = value.join(', ');
              } else {
                displayValue = `${value.length} items`;
              }
            } else {
              // For objects, try to show meaningful properties instead of raw JSON
              const meaningfulKeys = [
                'name',
                'dns',
                'identifier',
                'title',
                'type',
                'model',
              ];
              let foundMeaningful = false;

              for (const keyName of meaningfulKeys) {
                if (value[keyName] && typeof value[keyName] === 'string') {
                  displayValue = value[keyName];
                  foundMeaningful = true;
                  break;
                }
                // Check nested model object (common in target/origin structures)
                if (
                  value.model &&
                  value.model[keyName] &&
                  typeof value.model[keyName] === 'string'
                ) {
                  displayValue = value.model[keyName];
                  foundMeaningful = true;
                  break;
                }
              }

              if (!foundMeaningful) {
                // Fallback to truncated JSON
                displayValue = JSON.stringify(value).slice(0, 30) + '...';
              }
            }
          } else {
            // For strings
            const strValue = String(value);
            copyValue = strValue;
            displayValue =
              strValue.length > 60 ? strValue.slice(0, 60) + '...' : strValue;
          }

          return (
            <div className="group flex items-center justify-between">
              {isStatusField ? (
                <SmartStatusChip
                  status={String(value)}
                  priority={typeof priority === 'number' ? priority : undefined}
                  showSeverityInLabel={!isRiskData}
                />
              ) : (
                <span className="truncate">{displayValue}</span>
              )}
              <div className="ml-2 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
                <CopyToClipboard textToCopy={copyValue} alwaysShowCopyIcon />
              </div>
            </div>
          );
        },
      }));
    };

    return generateColumns(data);
  }, [data]);

  const displayName = tableName.charAt(0).toUpperCase() + tableName.slice(1);

  // Export using real API with async job, polling, and toast notifications
  const handleExport = async () => {
    // Extract keys from data items
    const items = data.map((item: any) => item.key).filter(Boolean);

    // If no keys, can't export
    if (items.length === 0) {
      console.warn('No keys found in data for export');
      return;
    }

    // Call export API - useAsyncExports handles job creation, polling, and notifications
    await exportData({
      items,
      format: 'csv',
      query: {} as any, // Required by type but not used when items provided
    });
  };

  return (
    <div className="border-chariot-stroke my-2 overflow-hidden rounded-lg border">
      <div className="bg-chariot-background-secondary border-chariot-stroke flex items-center justify-between border-b px-3 py-2">
        <span className="text-chariot-text-primary text-sm font-medium">
          {displayName} ({data.length})
        </span>
        <button
          type="button"
          onClick={handleExport}
          disabled={isExporting}
          className="text-chariot-text-secondary hover:text-chariot-text-primary flex items-center gap-1 px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="size-3" />
          {isExporting ? 'Exporting...' : 'Export'}
        </button>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <Table
          name={`chat-${tableName.toLowerCase()}`}
          data={data}
          columns={columns}
          status="success"
          tableClassName="border-0"
          error={null}
          selection={{ hide: true }}
        />
      </div>
    </div>
  );
};

export default ChatResultsTable;
